import React, { useState, useEffect, useCallback } from "react";

import { useParams } from 'react-router-dom';
import {
  Box,
  Input,
  FormControl,
  FormLabel,
  Text,
  Heading,
  Button,
  useToast,
  useColorModeValue,
  Flex,
  VStack,
  Tbody,
  Table,
  Tr,
  Td,
  Th,
  Thead,
  IconButton,
  HStack,
  Badge,
  Select,
  useDisclosure,

} from "@chakra-ui/react";
import axios from "axios";
import { FiTrash, FiCheck, FiX, FiEdit } from "react-icons/fi";
import { jwtDecode } from "jwt-decode";
import { PracticeSet,QuestionSet, PracticeSetQset, QuestionSetCategory,PracticeSetExam, PracticeSetExamQset } from '../../Platform/Common/types';
import { addMinutes } from 'date-fns'; // You can also manually add mins without this
import ReactSelect from 'react-select';
import { useNavigate } from "react-router-dom";
import { Product } from '../../Platform/Common/types';

interface PracticeSetExamQsetProps {
    practiceSetId:number;
    examId: number;
    isPsetEditing:boolean;
    allQuestionSets: QuestionSet[];
    onExamDeleted: (examId: number) => void; 
    canDelete: boolean;
  }

  
  const PracticeSetExamQsets: React.FC<PracticeSetExamQsetProps> = ({ practiceSetId, examId, isPsetEditing, allQuestionSets, onExamDeleted, canDelete }) => {
    // ────────────────────────────────────────────────────────────────────────────
    // Theme & routing hooks
    // ────────────────────────────────────────────────────────────────────────────

    const cardbg = useColorModeValue("blue.50", "blue.700");

    const navigate = useNavigate();
    const toast = useToast();

  
    // ────────────────────────────────────────────────────────────────────────────
    // State: core practiceSet, question-sets, mappings
    // ────────────────────────────────────────────────────────────────────────────

  
    // const [questionSets, setQuestionSets] = useState<QuestionSet[]>([]);

    const [practiceSetExam, setPracticeSetExam] = useState<PracticeSetExam>();

    const [isPracticeSetExamEdited, setIsPracticeSetExamEdited] = useState(false);
    
    const [questionSetCategories, setQuestionSetCategories] = useState<QuestionSetCategory[]>([]);
  
    type EditableMapping = PracticeSetExamQset & { isDraft?: boolean };

    const [mappings, setMappings] = useState<EditableMapping[]>([]);
    const [isMappingEdited, setIsMappingEdited] = useState(false);

    // ===== [QSET CATEGORY FILTER] per-row, UI-only category filter state START =====
    // Keyed by row index. NOT part of EditableMapping / PracticeSetExamQset --
    // never sent to the backend, never stored in mappings. Purely narrows
    // the Question Set dropdown's options for whichever row is currently
    // in draft/edit mode. Populated only while a row is being added or
    // edited, and cleaned up on confirm/cancel.
    const [categoryFilters, setCategoryFilters] = useState<Record<number, number>>({});
    // ===== [QSET CATEGORY FILTER] per-row, UI-only category filter state END =====
  
  
    // ────────────────────────────────────────────────────────────────────────────
    // Mode & button logic
    // ────────────────────────────────────────────────────────────────────────────
  

      const buttonBg = useColorModeValue(
        'linear-gradient(to right, blue.50, blue.100)',
        'linear-gradient(to right, blue.600, blue.700)'
      );

        const buttonHoverBg = useColorModeValue(
          'blue.200',
          'blue.500'
        );

  
    // ────────────────────────────────────────────────────────────────────────────
    // API fetchers
    // ────────────────────────────────────────────────────────────────────────────

    // Write fetchPracticeSetCategories, API has been written and then use Accordion logic - take help from ReviewCourseTerms


    const fetchPracticeSetExam = async () => {
      const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      const { data } = await axios.get<PracticeSetExam>(`${b2c_apiurl}/practice-set/${practiceSetId}/exam/${examId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
      });
      setPracticeSetExam(data);
    };

    const fetchQuestionSetCategories = async () => {
      const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      const { data } = await axios.get<QuestionSetCategory[]>(`${b2c_apiurl}/qset-categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
      });
      setQuestionSetCategories(data);
    };
  
    const fetchPracticeSetExamQsetMappings = async () => {
      const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      const { data } = await axios.get<PracticeSetExamQset[]>(`${b2c_apiurl}/practice-set/${practiceSetId}/exam/${examId}/qset-mappings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
      });
      setMappings(data);
    };
  

    // ────────────────────────────────────────────────────────────────────────────
    // Derived state effects
    // ────────────────────────────────────────────────────────────────────────────

    // 1) On load —  fetch everything
    useEffect(() => {
      (async () => {
        const token = localStorage.getItem("b2c_token");
        if (!token) return;
          await fetchPracticeSetExam();
/*           await fetchAllQuestionSets();
          await fetchAvailableQuestionSets(); */
          await fetchQuestionSetCategories();
          await fetchPracticeSetExamQsetMappings();

      })();
    }, [practiceSetId, examId]);

  

  
  // ────────────────────────────────────────────────────────────────────────────
// Mapping helpers
// ────────────────────────────────────────────────────────────────────────────

// helper to know if a draft row exists
const hasDraftMappingRow = () => mappings.some(m => m.isDraft);

// backup storage while editing an existing row
const [mappingBackups, setMappingBackups] = useState<Record<number, EditableMapping>>({});


const usedIds = new Set(
  mappings.map(m => m.question_set_id).filter(Boolean)
);

const remainingQSetCount = allQuestionSets.length - usedIds.size;


const isAddDisabled =
  hasDraftMappingRow() || remainingQSetCount <= 0;


// ────────────────────────────────────────────────────────────────────────────
// IMPORTANT: Practice Set uses persisted ordering → always maintain sequence_number
// This replaces the old "sequence" UI-only concept used in Exams.
// ────────────────────────────────────────────────────────────────────────────
const resequence = (rows: EditableMapping[]): EditableMapping[] =>
  rows.map((m, i) => ({
    ...m,
    sequence_number: i + 1,
  }));

// ===== [QSET CATEGORY FILTER] helper to remove one row's filter entry without destructuring-omit START =====
// A destructuring-omit pattern (const { [idx]: _, ...rest } = obj) on a
// Record<number, number> triggers a TS index-signature widening error
// (adds `| undefined`) under this project's tsconfig strictness. Using a
// shallow copy + delete avoids that inference issue while producing the
// identical result.
const removeCategoryFilter = (idx: number) => {
  setCategoryFilters(prev => {
    const next: Record<number, number> = { ...prev };
    delete next[idx];
    return next;
  });
};
// ===== [QSET CATEGORY FILTER] helper to remove one row's filter entry without destructuring-omit END =====

// ────────────────────────────────────────────────────────────────────────────
// Add a *draft* row (only if none exists)
// ────────────────────────────────────────────────────────────────────────────
const addMapping = () => {
  if (hasDraftMappingRow()) return;

  setMappings(prev =>
    resequence([
      ...prev,
      {
        exam_id: examId,
        question_set_id: 0,
        sequence_number: prev.length + 1,
        isDraft: true,
      } as EditableMapping,
    ])
  );

  setIsMappingEdited(true);
};


// ────────────────────────────────────────────────────────────────────────────
// Turn an existing row into edit mode
// ────────────────────────────────────────────────────────────────────────────
const editMapping = (idx: number) => {
  if (hasDraftMappingRow()) return;

  setMappingBackups(b => ({ ...b, [idx]: { ...mappings[idx] } }));

  // ===== [QSET CATEGORY FILTER] seed this row's filter from the qset's current category START =====
  // So editing an existing row doesn't reset the filter to blank -- the
  // dropdown opens already narrowed to the category the qset already
  // belongs to, matching what the user would expect to see.
  const currentQSet = allQuestionSets.find(
    (q) => q.question_set_id === mappings[idx].question_set_id
  );
  if (currentQSet) {
    setCategoryFilters(prev => ({ ...prev, [idx]: currentQSet.category_id } as Record<number, number>));
  }
  // ===== [QSET CATEGORY FILTER] seed this row's filter from the qset's current category END =====

  setMappings(prev =>
    prev.map((m, i) => (i === idx ? { ...m, isDraft: true } : m))
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Confirm the draft row
// ────────────────────────────────────────────────────────────────────────────
const confirmDraftMapping = (idx: number) => {
  const m = mappings[idx];

  if (!m.question_set_id) {
    toast({ title: "Please select a Question Set", status: "warning" });
    return;
  }

  setMappings(prev =>
    prev.map((m, i) => (i === idx ? { ...m, isDraft: false } : m))
  );

  // remove backup snapshot
  setMappingBackups(b => {
    const { [idx]: _, ...rest } = b;
    return rest;
  });

  // ===== [QSET CATEGORY FILTER] clear this row's filter -- not needed once confirmed =====
  // View mode derives the displayed category from the qset itself
  // (selectedCategory below), so the filter state has no further purpose
  // for this row once it's no longer a draft.
  removeCategoryFilter(idx);

  setIsMappingEdited(true);
};

// ────────────────────────────────────────────────────────────────────────────
// Cancel the draft row
// ────────────────────────────────────────────────────────────────────────────
const cancelDraftMapping = (idx: number) => {
  if (mappingBackups.hasOwnProperty(idx)) {
    // existing row → restore original
    setMappings(prev =>
      prev.map((m, i) =>
        i === idx ? { ...mappingBackups[idx], isDraft: false } : m
      )
    );

    setMappingBackups(b => {
      const { [idx]: _, ...rest } = b;
      return rest;
    });
  } else {
    // brand new row → remove it and resequence
    setMappings(prev => resequence(prev.filter((_, i) => i !== idx)));
  }

  // ===== [QSET CATEGORY FILTER] discard this row's filter on cancel =====
  removeCategoryFilter(idx);

  setIsMappingEdited(true);
};

// ────────────────────────────────────────────────────────────────────────────
// Delete mapping
// ────────────────────────────────────────────────────────────────────────────
const deleteMapping = (idx: number) => {
  setMappings(prev => resequence(prev.filter((_, i) => i !== idx)));
  setIsMappingEdited(true);
};

// ────────────────────────────────────────────────────────────────────────────
// Update mapping (no resequencing — order unchanged)
// ────────────────────────────────────────────────────────────────────────────
const updateMapping = (idx: number, updates: Partial<EditableMapping>) => {
  setMappings(prev =>
    prev.map((m, i) => (i === idx ? { ...m, ...updates } : m))
  );
  setIsMappingEdited(true);
};

// ===== [QSET CATEGORY FILTER] category filter change handler START =====
// Changing the category filter narrows the Question Set dropdown to that
// category. The previously selected question_set_id (if any) may not
// belong to the new category, so it's cleared to force a fresh, valid
// selection rather than silently leaving a stale/invalid qset selected.
// This never touches mapping.category or any persisted field -- category
// is not, and never becomes, part of the mapping payload sent to the API.
const handleCategoryFilterChange = (idx: number, categoryId: number) => {
  setCategoryFilters(prev => ({ ...prev, [idx]: categoryId } as Record<number, number>));
  updateMapping(idx, { question_set_id: 0 });
};
// ===== [QSET CATEGORY FILTER] category filter change handler END =====



const getAvailableQuestionSets = (currentIndex: number): QuestionSet[] => {
  const currentId = mappings[currentIndex]?.question_set_id;

  // IDs already used in this exam (except current row when editing)
  const usedIds = new Set(
    mappings
      .filter((_, idx) => idx !== currentIndex)
      .map(m => m.question_set_id)
      .filter(Boolean)
  );

  // Allow everything except what is already used
  let filtered = allQuestionSets.filter(
    qs => !usedIds.has(qs.question_set_id)
  );

  // ===== [QSET CATEGORY FILTER] narrow by this row's selected category, if any START =====
  // Optional convenience filter only -- if no category has been chosen
  // for this row yet, the full (within-exam-exclusivity-filtered) list is
  // shown unchanged, exactly as before this requirement existed. This
  // does NOT introduce any cross-exam exclusivity -- a qset used in
  // Exam 1 remains selectable in Exam 2, same as before.
  const categoryFilter = categoryFilters[currentIndex];
  if (categoryFilter) {
    filtered = filtered.filter(qs => qs.category_id === categoryFilter);
  }
  // ===== [QSET CATEGORY FILTER] narrow by this row's selected category, if any END =====

  // While editing — keep the already-selected one visible
  if (
    currentId &&
    !filtered.some(q => q.question_set_id === currentId)
  ) {
    const existing = allQuestionSets.find(
      q => q.question_set_id === currentId
    );
    if (existing) filtered.push(existing);
  }

  return filtered;
};


    // ────────────────────────────────────────────────────────────────────────────
    // Form handlers
    // ────────────────────────────────────────────────────────────────────────────
  
    // ────────────────────────────────────────────────────────────────────────────
    // Save / Create / Update / Publish / Cancel / Terminate
    // ────────────────────────────────────────────────────────────────────────────
        const handleUpdatePracticeSetExamQset = async () => {
          const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
          // guard

          try {
                await axios.put(`${b2c_apiurl}/practice-set/${practiceSetId}/exam/${examId}/update`, practiceSetExam, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
                });
                toast({ title: "PracticeSet Exam updated", status: "success" });
                setIsPracticeSetExamEdited(false);
   
                if (isMappingEdited) {
                await axios.delete(`${b2c_apiurl}/practice-set/${practiceSetId}/exam/${examId}/delete-mappings`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
                });
                await axios.post(`${b2c_apiurl}/practice-set/${practiceSetId}/exam/${examId}/create-mappings`, mappings, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
                });

                toast({ title: "Practice Set Exam Qset updated", status: "success" });
                }
    
            setIsMappingEdited(false);
            fetchPracticeSetExam();
  
          } catch (err) {
            console.error(err);
            toast({ title: "Error updating PracticeSetQsetGroup", status: "error" });
          }
        };
      

        const handleDeletePracticeSetExam = async () => {
          const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
          // guard

          try {

                await axios.delete(`${b2c_apiurl}/practice-set/${practiceSetId}/exam/${examId}/delete`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
                });

                toast({ title: "Practice Set Exam Deleted", status: "success" });

                onExamDeleted(examId);
  
          } catch (err) {
            console.error(err);
            toast({ title: "Error updating PracticeSetQsetGroup", status: "error" });
          }
        };
      

        const handleSavePracticeSet = () => {
          handleUpdatePracticeSetExamQset();
        };

        const handleInputChange = (e: React.ChangeEvent<any>) => {
              const { name, value } = e.target;
                setPracticeSetExam(prev => {
                    if (!prev) return prev; // data not loaded yet → do nothing safely

                    return {
                    ...prev,
                    [name]: value,
                    };
                });
              setIsPracticeSetExamEdited(true);
            };
        
  

  return (
    <Box  p={4}  borderRadius="md" as="main" mx="auto" bgColor={cardbg} boxShadow="md">
   
    <Box
    p={4}
    borderRadius="md"
    bg={cardbg}
    boxShadow="sm"
    >
                <Text
        fontSize="sm"
        fontWeight="bold"
        color="gray.500"
        minW="40px"
        pt="6px"
        >
        Exam #{practiceSetExam?.sequence_number} - Duration {practiceSetExam?.total_duration_minutes} Mins
        </Text>
    <Flex align="flex-start" gap={3}>
        {/* Sequence Badge */}

{/* Title + Description Area (takes remaining width) */}
<Box flex="1">
  {!isPsetEditing && (
    <>
      <Text fontSize="lg" fontWeight="semibold" lineHeight="1.2">
        {practiceSetExam?.title || "–"}
      </Text>

      <Text fontSize="sm" color="gray.500" mt={1}>
        {practiceSetExam?.description || "–"}
      </Text>

      <Text fontSize="sm" color="gray.500" mt={1}>
        Interval: {practiceSetExam?.interval_duration_minutes || "–"} minutes
      </Text>

      <Text fontSize="sm" color="gray.500" mt={1}>
        Passing Percentage: {practiceSetExam?.passing_percentage || "–"}%
      </Text>

      <Text fontSize="sm" color="gray.500" mt={1}>
        Max Attempts: {practiceSetExam?.max_attempts !== null ? practiceSetExam?.max_attempts : "Unlimited"}
      </Text>
    </>
  )}

  {isPsetEditing && practiceSetExam && (
    <VStack spacing={3} align="stretch">
      <FormControl>
        <FormLabel fontSize="sm" fontWeight="medium">Exam Title</FormLabel>
        <Input
          name="title"
          variant="flushed"
          fontSize="lg"
          fontWeight="semibold"
          px={0}
          value={practiceSetExam.title}
          onChange={handleInputChange}
          _focus={{ boxShadow: "none" }}
        />
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm" fontWeight="medium">Description</FormLabel>
        <Input
          name="description"
          variant="flushed"
          fontSize="sm"
          px={0}
          value={practiceSetExam.description}
          onChange={handleInputChange}
          _focus={{ boxShadow: "none" }}
        />
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm" fontWeight="medium">Interval Duration (minutes)</FormLabel>
        <Input
          name="interval_duration_minutes"
          type="number"
          min={0}
          variant="flushed"
          fontSize="sm"
          px={0}
          value={practiceSetExam.interval_duration_minutes}
          onChange={handleInputChange}
          _focus={{ boxShadow: "none" }}
        />
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm" fontWeight="medium">Passing Percentage (%)</FormLabel>
        <Input
          name="passing_percentage"
          type="number"
          min={0}
          max={100}
          variant="flushed"
          fontSize="sm"
          px={0}
          value={practiceSetExam.passing_percentage}
          onChange={handleInputChange}
          _focus={{ boxShadow: "none" }}
        />
      </FormControl>

    <FormControl>
      <FormLabel fontSize="sm" fontWeight="medium">
        Maximum Attempts (leave blank for unlimited)
      </FormLabel>
      <Input
        name="max_attempts"
        type="number"
        min={0}
        variant="flushed"
        fontSize="sm"
        px={0}
        value={practiceSetExam.max_attempts ?? ""}
        onChange={(e) => {
          const val = e.target.value;
          handleInputChange({
            target: {
              name: "max_attempts",
              value: val === "" ? null : Number(val),
            },
          } as any);
        }}
        _focus={{ boxShadow: "none" }}
        placeholder="Unlimited"
      />
    </FormControl>
    </VStack>
  )}
</Box>

        {/* Delete Icon — gets its OWN space now */}
        {isPsetEditing && canDelete && (
        <IconButton
            aria-label="Delete Exam"
            icon={<FiTrash />}
            variant="ghost"
            size="lg"
            color="gray.500"
            alignSelf="center"
            _hover={{ color: "red.500", bg: "transparent" }}
            onClick={() => {
            if (window.confirm("Delete this exam? This cannot be undone.")) {
                handleDeletePracticeSetExam();
            }
            }}
        />
        )}
    </Flex>
    </Box>

    

        {/* PracticeSet Exam Question Set Mapping */}

         <Box borderRadius="md" borderWidth={"thin"} mt={2} p={2}>
            
            {(mappings.length === 0)? (
            <Text fontSize="sm" color="gray.500" mt={3}>
                No question sets in this exam.
            </Text>
            ) : (

            <Table variant="simple" size="sm">
            <Thead>
                <Tr>
                {/* ===== [QSET CATEGORY FILTER] column order swapped: Category now first, Question Set second ===== */}
                <Th>Category</Th>
                <Th>Question Set</Th>
                {isPsetEditing && <Th>Actions</Th>}
                </Tr>
            </Thead>

            <Tbody>
                {mappings.map((mapping, index) => {
                const selectedQSet = allQuestionSets.find(
                    (qs) => qs.question_set_id === mapping.question_set_id
                );

                const selectedCategory = questionSetCategories.find(
                    (cat) => cat.category_id === selectedQSet?.category_id
                );

                return (
                    <Tr key={index}>
                    {/* ===== [QSET CATEGORY FILTER] Category cell moved first; draft rows get an editable filter dropdown START ===== */}
                    <Td>
                        {isPsetEditing && mapping.isDraft ? (
                        <Select
                            size="sm"
                            placeholder="Select category"
                            value={categoryFilters[index] ?? ''}
                            onChange={(e) =>
                              handleCategoryFilterChange(index, Number(e.target.value))
                            }
                        >
                            {questionSetCategories.map((cat) => (
                            <option key={cat.category_id} value={cat.category_id}>
                                {cat.category_name}
                            </option>
                            ))}
                        </Select>
                        ) : (
                        selectedCategory?.category_name || "–"
                        )}
                    </Td>
                    {/* ===== [QSET CATEGORY FILTER] Category cell moved first; draft rows get an editable filter dropdown END ===== */}

                    {/* Question Set */}
                    <Td>
                        {isPsetEditing ? (
                        <Select
                            size="sm"
                            placeholder="Select question set"
                            value={mapping.question_set_id || ""}
                            isDisabled={!mapping.isDraft}
                            onChange={(e) =>
                            updateMapping(index, {
                                question_set_id: Number(e.target.value),
                            })
                            }
                        >
                            {getAvailableQuestionSets(index).map((qs) => (
                            <option key={qs.question_set_id} value={qs.question_set_id}>
                                {qs.title}
                            </option>
                            ))}
                        </Select>
                        ) : (
                        selectedQSet?.title || "–"
                        )}
                    </Td>

                    {/* Actions — ONLY in edit mode */}
                    {isPsetEditing && (
                        <Td>
                        {mapping.isDraft ? (
                            <HStack spacing={1}>
                            <IconButton
                                aria-label="Confirm"
                                icon={<FiCheck />}
                                size="sm"
                                colorScheme="green"
                                isDisabled={!mapping.question_set_id}
                                onClick={() => confirmDraftMapping(index)}
                            />
                            <IconButton
                                aria-label="Cancel"
                                icon={<FiX />}
                                size="sm"
                                colorScheme="red"
                                onClick={() => cancelDraftMapping(index)}
                            />
                            </HStack>
                        ) : (
                            <HStack spacing={1}>
                            <IconButton
                                aria-label="Edit"
                                icon={<FiEdit />}
                                colorScheme="blue"
                                size="sm"
                                onClick={() => editMapping(index)}
                                isDisabled={hasDraftMappingRow()}
                            />
                            <IconButton
                                aria-label="Delete"
                                icon={<FiTrash />}
                                size="sm"
                                colorScheme="red"
                                onClick={() => {
                                if (
                                    window.confirm(
                                    "Are you sure you want to remove this Question Set?"
                                    )
                                ) {
                                    deleteMapping(index);
                                }
                                }}
                                isDisabled={hasDraftMappingRow()}
                            />
                            </HStack>
                        )}
                        </Td>
                    )}
                    </Tr>
                );
                })}
            </Tbody>
            </Table>

             )}
                {(isPsetEditing) && ( 
                    <Button
                        onClick={addMapping}
                        size="xs"
                        borderRadius="sm"
                        colorScheme="cyan"
                        mt={2} ml={4}
                        isDisabled={isAddDisabled}
                        >
                        + Q Set
                        </Button>
                        )} 
        </Box>
        
            {(isPsetEditing) && ( 
                    <Button
                    mt={2}
                    colorScheme="teal"
                    size="sm"
                    borderRadius="sm"
                    onClick={handleSavePracticeSet}
                    isDisabled={ (!isMappingEdited || hasDraftMappingRow()) && !isPracticeSetExamEdited}
                    >
                    Commit
                    </Button>
                    )} 
                            
    </Box>
  );
};

export default PracticeSetExamQsets;